/**
 * Weekly outreach volume by language — answers "how fast is outreach growing,
 * and which languages are getting the volume?"
 */

import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useOutreachVelocity } from '@/hooks/ml';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#0ea5e9', '#a855f7', '#14b8a6'];

export const OutreachVelocityCard: React.FC = () => {
  const [weeks, setWeeks] = useState(26);
  const [metric, setMetric] = useState<'sends' | 'engagements'>('sends');
  const { data, isLoading } = useOutreachVelocity(weeks);

  // Pivot points to {week: {lang: value}} for Recharts
  const { series, langs } = useMemo(() => {
    const points = data?.points ?? [];
    if (!points.length) return { series: [], langs: [] };
    // Pick the top 6 languages by total volume
    const totals: Record<string, number> = {};
    for (const p of points) {
      totals[p.message_language] = (totals[p.message_language] || 0) + p[metric];
    }
    const topLangs = Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([k]) => k);
    const byWeek = new Map<string, Record<string, number>>();
    for (const p of points) {
      if (!topLangs.includes(p.message_language)) continue;
      const wk = byWeek.get(p.send_week) ?? {};
      wk[p.message_language] = (wk[p.message_language] || 0) + p[metric];
      byWeek.set(p.send_week, wk);
    }
    const series = Array.from(byWeek.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([wk, byLang]) => ({
        week: format(parseISO(wk), 'MMM d'),
        ...byLang,
      }));
    return { series, langs: topLangs };
  }, [data, metric]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="text-xs text-gray-500">
          Weekly outreach volume by language. Top 6 languages plotted.
        </div>
        <div className="flex items-center gap-2">
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as 'sends' | 'engagements')}
            className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
          >
            <option value="sends">Sends</option>
            <option value="engagements">Engaged</option>
          </select>
          <select
            value={weeks}
            onChange={(e) => setWeeks(parseInt(e.target.value, 10))}
            className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
          >
            <option value={13}>Last 13 wk</option>
            <option value={26}>Last 26 wk</option>
            <option value={52}>Last 52 wk</option>
          </select>
        </div>
      </div>
      {isLoading ? (
        <div className="h-64 flex items-center justify-center text-sm text-gray-500">Loading…</div>
      ) : series.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-sm text-gray-500">
          No outreach activity in the selected window.
        </div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="week" stroke="#6b7280" tick={{ fontSize: 11 }} minTickGap={32} />
              <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} allowDecimals={false} width={48} />
              <Tooltip
                formatter={(value, name) => [Number(value).toLocaleString(), String(name)]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {langs.map((lang, i) => (
                <Line
                  key={lang}
                  type="monotone"
                  dataKey={lang}
                  stroke={COLORS[i % COLORS.length]}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                  name={lang}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
